import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MonthCalendar, CalendarLegend } from "@/components/MonthCalendar";
import { PageHeader } from "@/components/admin/ui";
import { DayPanel } from "@/components/admin/DayPanel";
import { StatusBadge } from "@/components/common";
import { useAdminTrips } from "@/hooks/queries";
import { fetcher, invalidateAdmin } from "@/lib/api";
import { eachDay, formatRange, monthBounds, startOfMonth, todayISO } from "@/lib/format";

const buildIndex = (data, start, end) => {
  const idx = {};
  const at = (d) => (idx[d] = idx[d] || { orders: [], blocked: [] });
  (data?.orders || []).forEach((o) =>
    eachDay(o.start_date < start ? start : o.start_date, o.end_date > end ? end : o.end_date).forEach((d) => at(d).orders.push(o)),
  );
  (data?.blocked || []).forEach((b) => at(b.date).blocked.push(b));
  return idx;
};

const MonthOrders = ({ orders }) => (
  <div className="panel p-5 sm:p-6" data-testid="admin-month-orders">
    <h3 className="mb-4 font-display text-xl">Pesanan bulan ini</h3>
    {orders.length === 0 && <p className="text-sm text-neutral-500">Belum ada pesanan di bulan ini.</p>}
    <ul className="divide-y divide-white/5">
      {orders.map((o) => (
        <li key={o.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
          <span className="font-mono text-gold-light">{o.code}</span>
          <span className="min-w-0 flex-1 truncate">{o.trip_name} · <span className="text-neutral-500">{o.customer_name}</span></span>
          <span className="whitespace-nowrap text-neutral-400">{formatRange(o.start_date, o.end_date)}</span>
          <StatusBadge status={o.status} />
        </li>
      ))}
    </ul>
  </div>
);

export default function Schedule() {
  const qc = useQueryClient();
  const { data: trips = [] } = useAdminTrips();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(todayISO());
  const { start, end } = monthBounds(month);
  const { data } = useQuery({ queryKey: ["admin-schedule", start, end], queryFn: () => fetcher("/admin/schedule", { start, end }) });
  const index = useMemo(() => buildIndex(data, start, end), [data, start, end]);
  const today = todayISO();

  const getDay = (iso) => {
    const e = index[iso];
    let state = iso < today ? "past" : "available";
    if (e?.orders.length) state = "booked";
    else if (e?.blocked.length) state = "blocked";
    return { state, selected: iso === selected, badge: e?.orders.length ? String(e.orders.length) : null };
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule-page">
      <PageHeader title="Jadwal" subtitle="Semua tanggal terisi, kosong, dan yang ditutup" />
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="panel p-5 sm:p-6 xl:col-span-8">
          <MonthCalendar month={month} onMonthChange={(m) => { setMonth(m); setSelected(null); }} getDay={getDay} onSelect={setSelected} testId="admin-calendar" size="lg" />
          <CalendarLegend items={["available", "booked", "blocked"]} />
        </div>
        <div className="panel p-5 sm:p-6 xl:col-span-4">
          <DayPanel date={selected} entry={index[selected]} trips={trips} onChanged={() => invalidateAdmin(qc)} />
        </div>
      </div>
      <MonthOrders orders={data?.orders || []} />
    </div>
  );
}
